import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bot, Check, ExternalLink, Loader2 } from "lucide-react";
import { getApiBase } from "@/lib/httpClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Parâmetros que o retorno do OAuth e a faixa do chat deixam na URL.
const PARAMETROS_DE_RETORNO = ["conectado", "erro", "ativar"];

export function rotuloDoModelo(item) {
  if (!item) return "";
  if (item.entrada == null || item.saida == null) return item.nome;
  if (item.entrada === 0 && item.saida === 0) return `${item.nome} · gratuito`;
  return `${item.nome} · US$ ${item.entrada} / ${item.saida} por 1M tokens`;
}

export function filtrarModelos(modelos, termo) {
  const busca = (termo || "").trim().toLowerCase();
  if (!busca) return modelos;
  return modelos.filter(
    (m) => m.id.toLowerCase().includes(busca) || m.nome.toLowerCase().includes(busca),
  );
}

export function ehGratuito(item) {
  return item?.entrada === 0 && item?.saida === 0;
}

// O OpenRouter escreve "NVIDIA: Nemotron 3 Nano" e "nvidia/nemotron-3-nano".
// O nome traz o fabricante como o próprio fabricante se escreve; o id é o
// reserva para quando o nome vem sem prefixo.
export function fabricanteDoModelo(item) {
  const doNome = /^([^:]{1,40}):\s/.exec(item?.nome || "");
  if (doNome) return doNome[1].trim();
  const doId = String(item?.id || "").split("/");
  return doId.length > 1 ? doId[0] : "Outros";
}

export function fabricantes(modelos) {
  const contagem = new Map();
  for (const m of modelos) {
    const nome = fabricanteDoModelo(m);
    contagem.set(nome, (contagem.get(nome) || 0) + 1);
  }
  return [...contagem.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], "pt-BR"))
    .map(([nome, quantos]) => ({ nome, quantos }));
}

export function aplicarFiltros(modelos, { termo = "", fabricante = "", preco = "" } = {}) {
  let lista = filtrarModelos(modelos, termo);
  if (fabricante) lista = lista.filter((m) => fabricanteDoModelo(m) === fabricante);
  if (preco === "gratis") lista = lista.filter(ehGratuito);
  if (preco === "pago") lista = lista.filter((m) => m.entrada != null && !ehGratuito(m));
  return lista;
}

export function precoDoModelo(item) {
  if (item?.entrada == null || item?.saida == null) return "—";
  if (ehGratuito(item)) return "gratuito";
  return `US$ ${item.entrada} / ${item.saida}`;
}

function contextoDoModelo(item) {
  if (!item?.contexto) return "—";
  return item.contexto >= 1000 ? `${Math.round(item.contexto / 1000)}k` : String(item.contexto);
}

// Centenas de modelos numa lista rolável viram um borrão. Em tabela, cada
// linha traz o que decide a escolha — fabricante, preço e janela — e os
// filtros cortam o catálogo antes de o advogado precisar ler tudo.
function TabelaModelos({ id, modelos, valor, onChange }) {
  const [termo, setTermo] = useState("");
  const [fabricante, setFabricante] = useState("");
  const [preco, setPreco] = useState("");

  const marcas = useMemo(() => fabricantes(modelos), [modelos]);
  const visiveis = useMemo(
    () => aplicarFiltros(modelos, { termo, fabricante, preco }),
    [modelos, termo, fabricante, preco],
  );
  const escolhido = modelos.find((m) => m.id === valor);
  const escolhidoEscondido = escolhido && !visiveis.includes(escolhido);

  return (
    <div className="mt-1.5" data-testid={id}>
      <div className="flex flex-wrap gap-2">
        <Input
          data-testid={`${id}-busca`}
          value={termo}
          onChange={(evento) => setTermo(evento.target.value)}
          placeholder="Buscar por nome (ex.: claude, gpt, gemini)"
          autoComplete="off"
          aria-label="Buscar modelo"
          className="flex-1 min-w-[12rem]"
        />
        <select
          data-testid={`${id}-fabricante`}
          value={fabricante}
          onChange={(evento) => setFabricante(evento.target.value)}
          aria-label="Filtrar por fabricante"
          className="h-10 border border-[#D1DEEE] rounded-md px-3 text-[#001533] bg-white"
        >
          <option value="">Todos os fabricantes</option>
          {marcas.map((m) => (
            <option key={m.nome} value={m.nome}>{`${m.nome} (${m.quantos})`}</option>
          ))}
        </select>
        <select
          data-testid={`${id}-preco`}
          value={preco}
          onChange={(evento) => setPreco(evento.target.value)}
          aria-label="Filtrar por preço"
          className="h-10 border border-[#D1DEEE] rounded-md px-3 text-[#001533] bg-white"
        >
          <option value="">Gratuitos e pagos</option>
          <option value="gratis">Só gratuitos</option>
          <option value="pago">Só pagos</option>
        </select>
      </div>

      <div className="mt-2 border border-[#D1DEEE] rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-[#F4F7FB] z-10">
              <tr className="text-left text-[#335480]">
                <th scope="col" className="font-medium px-3 py-2">Modelo</th>
                <th scope="col" className="font-medium px-3 py-2 hidden sm:table-cell">Fabricante</th>
                <th scope="col" className="font-medium px-3 py-2 whitespace-nowrap">
                  Preço <span className="text-[#6685AD]">/ 1M tokens</span>
                </th>
                <th scope="col" className="font-medium px-3 py-2 hidden md:table-cell">Contexto</th>
                <th scope="col" className="px-3 py-2"><span className="sr-only">Ação</span></th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((m) => {
                const selecionado = m.id === valor;
                return (
                  <tr
                    key={m.id}
                    data-testid={`${id}-linha`}
                    data-modelo={m.id}
                    aria-selected={selecionado}
                    className={`border-t border-[#D1DEEE] ${selecionado ? "bg-[#e5edf5]" : "hover:bg-[#F4F7FB]"}`}
                  >
                    <td className="px-3 py-2 text-[#001533]">
                      <span className="block font-medium">{m.nome}</span>
                      <span className="block text-[#6685AD] text-xs">{m.id}</span>
                    </td>
                    <td className="px-3 py-2 text-[#335480] hidden sm:table-cell">{fabricanteDoModelo(m)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {ehGratuito(m) ? (
                        <span className="inline-block rounded-full bg-[#e5edf5] text-[#003882] px-2 py-0.5 text-xs font-medium">
                          gratuito
                        </span>
                      ) : (
                        <span className="text-[#335480] tabular-nums">{precoDoModelo(m)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#335480] hidden md:table-cell tabular-nums">{contextoDoModelo(m)}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        data-testid={`${id}-selecionar`}
                        onClick={() => onChange(m.id)}
                        aria-label={`Selecionar ${m.nome}`}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium border whitespace-nowrap ${
                          selecionado
                            ? "border-[#003882] text-[#003882] bg-white"
                            : "border-[#D1DEEE] text-[#335480] hover:border-[#003882] hover:text-[#003882]"
                        }`}
                      >
                        {selecionado ? "Selecionado" : "Selecionar"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {visiveis.length === 0 && (
            <p className="text-[#335480] text-sm px-3 py-6 text-center" data-testid={`${id}-vazio`}>
              Nenhum modelo com esses filtros.
            </p>
          )}
        </div>
      </div>

      <p className="text-[#6685AD] text-sm mt-1.5" data-testid={`${id}-resumo`}>
        {visiveis.length} de {modelos.length} modelos
        {escolhidoEscondido ? ` · escolhido: ${escolhido.nome}` : ""}
      </p>
    </div>
  );
}

// Poucos modelos não justificam tabela: o select resolve em uma linha.
function SeletorModelo({ id, modelos, valor, onChange, carregando }) {
  if (carregando) {
    return (
      <p className="mt-1.5 text-[#335480] flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Carregando modelos...
      </p>
    );
  }
  if (modelos.length > 12) {
    return <TabelaModelos id={id} modelos={modelos} valor={valor} onChange={onChange} />;
  }
  return (
    <select
      id={id}
      data-testid={id}
      value={valor}
      onChange={(evento) => onChange(evento.target.value)}
      className="mt-1.5 w-full h-10 border border-[#D1DEEE] rounded-md px-3 text-[#001533] bg-white"
    >
      {!valor && <option value="">Escolha um modelo</option>}
      {modelos.map((m) => (
        <option key={m.id} value={m.id}>{rotuloDoModelo(m)}</option>
      ))}
    </select>
  );
}

export default function ConexaoIA({ isOwner }) {
  const [catalogo, setCatalogo] = useState(null);
  const [conexao, setConexao] = useState(null);
  const [provedor, setProvedor] = useState("openrouter");
  const [modelos, setModelos] = useState([]);
  const [carregandoModelos, setCarregandoModelos] = useState(false);
  const [modelo, setModelo] = useState("");
  const [chave, setChave] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [formato, setFormato] = useState("openai");
  const [salvando, setSalvando] = useState(false);
  const [destacar, setDestacar] = useState(false);
  const secao = useRef(null);
  const retornoTratado = useRef(false);

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${getApiBase()}/chat/provedores`, { withCredentials: true });
      setCatalogo(data.provedores || []);
      setConexao(data.conexao || null);
      if (data.conexao) {
        setProvedor(data.conexao.provedor);
        setModelo(data.conexao.modelo || "");
      }
    } catch {
      setCatalogo([]);
    }
  }, []);

  useEffect(() => { if (isOwner) carregar(); }, [isOwner, carregar]);

  const atual = (catalogo || []).find((p) => p.chave === provedor);
  const temOauth = atual?.metodos?.includes("oauth");
  const livre = Boolean(atual?.exige_url);

  // Modelos do provedor escolhido. No custom o catálogo é do endpoint do usuário.
  useEffect(() => {
    if (!isOwner || !provedor || livre) { setModelos([]); return undefined; }
    let ativo = true;
    setCarregandoModelos(true);
    axios
      .get(`${getApiBase()}/chat/modelos/${provedor}`, { withCredentials: true })
      .then(({ data }) => { if (ativo) setModelos(data.modelos || []); })
      .catch(() => { if (ativo) setModelos([]); })
      .finally(() => { if (ativo) setCarregandoModelos(false); });
    return () => { ativo = false; };
  }, [isOwner, provedor, livre]);

  // Retorno do OAuth e faixa do chat. Roda uma vez só: antes, recarregar o
  // catálogo disparava o efeito de novo, a URL ainda tinha `conectado`, e a
  // tela entrava em laço empilhando o mesmo aviso.
  useEffect(() => {
    if (retornoTratado.current || catalogo === null) return undefined;
    retornoTratado.current = true;
    const params = new URLSearchParams(window.location.search);
    const conectado = params.get("conectado") === "openrouter";
    const falhou = params.get("erro") === "openrouter";
    const ativar = params.get("ativar") === "chat";

    if (PARAMETROS_DE_RETORNO.some((p) => params.has(p))) {
      PARAMETROS_DE_RETORNO.forEach((p) => params.delete(p));
      const resto = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (resto ? `?${resto}` : ""));
    }
    if (conectado) toast.success("Conta do OpenRouter conectada.");
    if (falhou) toast.error("Não foi possível concluir a conexão com o OpenRouter.");
    if (!ativar || !secao.current) return undefined;
    secao.current.scrollIntoView({ behavior: "smooth", block: "center" });
    setDestacar(true);
    const timer = setTimeout(() => setDestacar(false), 3000);
    return () => clearTimeout(timer);
  }, [catalogo]);

  if (!isOwner) return null;

  const trocarProvedor = (novo) => {
    setProvedor(novo);
    const cfg = (catalogo || []).find((p) => p.chave === novo);
    setModelo(cfg?.exige_url ? "" : cfg?.modelo_padrao || "");
  };

  const conectar = async () => {
    setSalvando(true);
    try {
      const { data } = await axios.post(
        `${getApiBase()}/chat/conectar`,
        {
          provedor,
          api_key: chave.trim(),
          modelo,
          ...(livre ? { base_url: baseUrl.trim(), formato } : {}),
        },
        { withCredentials: true },
      );
      setChave("");
      setConexao(data);
      toast.success("Modelo conectado.");
    } catch (erro) {
      toast.error(erro?.response?.data?.detail?.message || "Não foi possível conectar.");
    } finally {
      setSalvando(false);
    }
  };

  const conectarOpenRouter = async () => {
    setSalvando(true);
    try {
      const { data } = await axios.get(`${getApiBase()}/chat/openrouter/conectar`, {
        withCredentials: true,
        params: { modelo },
      });
      window.location.href = data.url;
    } catch (erro) {
      toast.error(erro?.response?.data?.detail?.message || "Não foi possível iniciar a conexão com o OpenRouter.");
      setSalvando(false);
    }
  };

  const salvarModelo = async () => {
    setSalvando(true);
    try {
      const { data } = await axios.patch(
        `${getApiBase()}/chat/modelo`, { modelo: modelo.trim() }, { withCredentials: true },
      );
      setConexao((c) => ({ ...c, modelo: data.modelo }));
      toast.success("Modelo atualizado.");
    } catch (erro) {
      toast.error(erro?.response?.data?.detail?.message || "Não foi possível trocar o modelo.");
    } finally {
      setSalvando(false);
    }
  };

  const desconectar = async () => {
    if (!window.confirm("Desconectar o modelo? O assistente para de responder até você conectar outro.")) return;
    try {
      await axios.delete(`${getApiBase()}/chat/conexao`, { withCredentials: true });
      setConexao(null);
      trocarProvedor(provedor);
      toast.success("Modelo desconectado.");
    } catch {
      toast.error("Não foi possível desconectar.");
    }
  };

  const campoModelo = (id) => (
    livre ? (
      <Input
        id={id}
        data-testid={id}
        value={modelo}
        onChange={(evento) => setModelo(evento.target.value)}
        placeholder="claude-sonnet-5"
        autoComplete="off"
        className="mt-1.5"
      />
    ) : (
      <SeletorModelo
        id={id}
        modelos={modelos}
        valor={modelo}
        onChange={setModelo}
        carregando={carregandoModelos}
      />
    )
  );

  const modeloMudou = conexao && modelo.trim() && modelo.trim() !== conexao.modelo;

  return (
    <div
      id="chat"
      ref={secao}
      data-testid="secao-conexao-ia"
      className={`bg-white border rounded-lg p-6 animate-fade-in-2 transition-colors ${
        destacar ? "border-[#003882] bg-[#F4F7FB]" : "border-[#D1DEEE]"
      }`}
    >
      <div className="flex items-center gap-3 mb-1">
        <Bot size={20} strokeWidth={1.5} className={conexao ? "text-[#003882]" : "text-[#335480]"} aria-hidden="true" />
        <div>
          <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            Assistente de IA
          </h2>
          <p className="text-[#335480] text-sm mt-0.5">
            Conecte a conta que você já paga. O consumo é cobrado direto no seu provedor — o TUTO não cobra por mensagem.
          </p>
        </div>
      </div>

      {conexao ? (
        <div className="mt-5 space-y-4" data-testid="ia-conectada">
          <div className="flex flex-wrap items-center justify-between gap-3 border border-[#D1DEEE] rounded-lg p-4 bg-[#F4F7FB]">
            <p className="text-[#001533]">
              <Check size={16} strokeWidth={1.5} className="inline text-[#003882] mr-1.5" aria-hidden="true" />
              Conectado a <strong>{(catalogo || []).find((p) => p.chave === conexao.provedor)?.nome || conexao.provedor}</strong>
              {conexao.metodo === "oauth" ? <span className="text-[#6685AD]"> · via OAuth</span> : null}
              {conexao.base_url ? <span className="block text-[#6685AD] text-sm mt-1">{conexao.base_url}</span> : null}
            </p>
            <Button
              variant="outline"
              data-testid="ia-desconectar"
              onClick={desconectar}
              className="border-[#D1DEEE] text-[#335480] hover:text-[#003882]"
            >
              Desconectar
            </Button>
          </div>

          <div>
            <Label htmlFor="ia-modelo-atual" className="text-[#335480]">
              Modelo em uso{conexao.modelo ? `: ${conexao.modelo}` : ""}
            </Label>
            {campoModelo("ia-modelo-atual")}
          </div>
          <Button
            data-testid="ia-salvar-modelo"
            onClick={salvarModelo}
            disabled={!modeloMudou || salvando}
            className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
          >
            {salvando ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
            Usar este modelo
          </Button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div>
            <Label htmlFor="ia-provedor" className="text-[#335480]">Provedor</Label>
            <select
              id="ia-provedor"
              data-testid="ia-provedor"
              value={provedor}
              onChange={(evento) => trocarProvedor(evento.target.value)}
              className="mt-1.5 w-full h-10 border border-[#D1DEEE] rounded-md px-3 text-[#001533] bg-white"
            >
              {(catalogo || []).map((p) => (
                <option key={p.chave} value={p.chave}>{p.nome}</option>
              ))}
            </select>
            {atual?.ajuda && <p className="text-[#335480] text-sm mt-1.5">{atual.ajuda}</p>}
          </div>

          {livre && (
            <>
              <div>
                <Label htmlFor="ia-base-url" className="text-[#335480]">URL base</Label>
                <Input
                  id="ia-base-url"
                  data-testid="ia-base-url"
                  value={baseUrl}
                  onChange={(evento) => setBaseUrl(evento.target.value)}
                  placeholder="https://routellm.abacus.ai/v1"
                  autoComplete="off"
                  className="mt-1.5"
                />
                <p className="text-[#335480] text-sm mt-1.5">Precisa ser https e acessível pela internet.</p>
              </div>
              <div>
                <Label htmlFor="ia-formato" className="text-[#335480]">Protocolo do endpoint</Label>
                <select
                  id="ia-formato"
                  data-testid="ia-formato"
                  value={formato}
                  onChange={(evento) => setFormato(evento.target.value)}
                  className="mt-1.5 w-full h-10 border border-[#D1DEEE] rounded-md px-3 text-[#001533] bg-white"
                >
                  <option value="openai">Compatível com OpenAI (/chat/completions)</option>
                  <option value="anthropic">Compatível com Anthropic (/messages)</option>
                </select>
              </div>
            </>
          )}

          {/* O modelo vem antes do botão de OAuth: a escolha viaja com o clique. */}
          <div>
            <Label htmlFor="ia-modelo" className="text-[#335480]">Modelo</Label>
            {campoModelo("ia-modelo")}
          </div>

          {temOauth && (
            <div className="border border-[#003882]/20 bg-[#e5edf5] rounded-lg p-4">
              <p className="text-[#001533] mb-3">
                Conecte sua conta do OpenRouter com um clique, usando o modelo escolhido acima.
              </p>
              <Button
                data-testid="ia-conectar-oauth"
                onClick={conectarOpenRouter}
                disabled={salvando || !modelo}
                className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
              >
                {salvando ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <ExternalLink size={16} strokeWidth={1.5} aria-hidden="true" />}
                Conectar com OpenRouter
              </Button>
              <p className="text-[#335480] text-sm mt-3">Ou cole uma chave abaixo.</p>
            </div>
          )}

          <div>
            <Label htmlFor="ia-chave" className="text-[#335480]">Chave de API</Label>
            <Input
              id="ia-chave"
              data-testid="ia-chave"
              type="password"
              value={chave}
              onChange={(evento) => setChave(evento.target.value)}
              placeholder="Cole aqui a chave do provedor"
              autoComplete="off"
              className="mt-1.5"
            />
            <p className="text-[#335480] text-sm mt-1.5">
              A chave é guardada cifrada e nunca volta para esta tela. Validamos com o provedor antes de salvar.
            </p>
            {/* Criar a chave é o passo onde o advogado empaca: o caminho até ela
                fica aqui, e não numa busca fora do TUTO. */}
            {atual?.tutorial && (
              <a
                href={atual.tutorial}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ia-tutorial-chave"
                className="inline-flex items-center gap-1.5 text-[#003882] font-medium text-sm mt-2 hover:underline"
              >
                <ExternalLink size={14} strokeWidth={1.5} aria-hidden="true" />
                Como criar a chave no {atual.nome}
              </a>
            )}
          </div>

          <Button
            data-testid="ia-conectar"
            onClick={conectar}
            disabled={!chave.trim() || !modelo.trim() || salvando || (livre && !baseUrl.trim())}
            className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
          >
            {salvando ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
            {salvando ? "Validando..." : "Conectar"}
          </Button>
        </div>
      )}
    </div>
  );
}
